import _ from 'lodash'
import Cookie from 'js-cookie'
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'

import lang from 'lang'
import { httpRequestWithoutRedirectionOnFailure } from 'services/saga'
import { getApiUrl } from 'services/selectors'
import { validateField, validateFields } from 'common/validator'
import { requests } from 'common/requests'
import { loginSaga } from 'common/services/login/saga'

import {
  ACTIVATION_REQUEST,
  activationFailure,
  activationSuccess,
  addFieldError,
  clearFieldError,
  VALIDATE_ACTIVATION_FIELD,
} from './actions'
import type { ActivationRequestPayload } from './actions'

interface ActivationSagaPayload {
  payload: ActivationRequestPayload
}

function* activationSaga(action: ActivationSagaPayload): Generator<unknown, void, unknown> {
  const { payload } = action
  if (payload.password.value !== payload.password2.value) {
    yield put(activationFailure({ base: 'Passwords mismatch!' }))
    return
  }

  const errors = validateFields(payload as unknown as Parameters<typeof validateFields>[0])

  if (_.isEmpty(errors)) {
    const apiURL = (yield select(getApiUrl)) as string
    try {
      const { data } = (yield call(httpRequestWithoutRedirectionOnFailure, {
        req: requests.post,
        url: `${apiURL}/rpc/activate_account`,
        data: _.mapValues(_.omit(payload, 'token'), 'value'),
        headers: {
          Authorization: `Bearer ${payload.token.value}`,
          Prefer: 'params=single-object',
        },
      })) as { data: { token: string; me: { email: string } } }
      Cookie.set('ruler-jwt', data.token)
      yield put(activationSuccess(data))
      yield call(loginSaga, {
        payload: {
          email: { value: data.me.email },
          password: { value: payload.password.value },
          rememberMe: { value: true },
        },
      })
    } catch (error: unknown) {
      yield put(
        activationFailure({
          base: _.get(error, 'response.data.message', lang.unhandledServerError) as string,
        }),
      )
    }
  } else {
    yield put(activationFailure(errors as Record<string, string>))
  }
}

export function* authenticateUser(action: {
  payload: {
    jwt: string
    res: { writeHead: (code: number, headers: Record<string, string>) => void; end: () => void }
  }
}): Generator<unknown, void, unknown> {
  const { jwt, res } = action.payload
  const apiURL = (yield select(getApiUrl)) as string
  try {
    const { data } = (yield call(httpRequestWithoutRedirectionOnFailure, {
      req: requests.post,
      url: `${apiURL}/rpc/me`,
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    })) as { data: { status?: string } }
    if (data.status !== 'disabled') {
      res.writeHead(302, { Location: '/login' })
      res.end()
    }
  } catch {
    // Invalid cookie, we won't do anything for the user
  }
}

export function* validateFieldSaga(action: {
  payload: { field: string; value: unknown; validations: string }
}): Generator<unknown, void, unknown> {
  const { field, value, validations } = action.payload
  const error = validateField({ field, value, fieldValidations: validations })

  if (error) {
    yield put(addFieldError({ field, error: typeof error === 'string' ? error : String(error) }))
  } else {
    yield put(clearFieldError({ field }))
  }
}

export default function* saga(): Generator<unknown, void, unknown> {
  yield all([
    // @ts-expect-error redux-saga types expect ActionPattern; string action type is valid at runtime
    takeLatest(ACTIVATION_REQUEST, activationSaga),
    // @ts-expect-error redux-saga types expect ActionPattern; string action type is valid at runtime
    takeEvery(VALIDATE_ACTIVATION_FIELD, validateFieldSaga),
  ])
}
